import * as XLSX from 'xlsx';
import { PriceChange, PRICE_TYPE_LABELS } from '../types';

const formatDateTime = (timestamp: string): string => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'N/A';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatCurrency = (value: number): string => {
  return `$${Math.round(value).toLocaleString('es-CL')}`;
};

const calculatePercentageChange = (oldPrice: number, newPrice: number): string => {
  if (oldPrice === 0) return 'N/A';
  const change = ((newPrice - oldPrice) / oldPrice) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
};

const getChangeType = (oldPrice: number, newPrice: number): string => {
  if (newPrice > oldPrice) return 'Aumento';
  if (newPrice < oldPrice) return 'Disminución';
  return 'Sin cambio';
};

const transformPriceChangesData = (changes: PriceChange[]) => {
  return changes.map(change => ({
    'ID Producto': change.product_id,
    'Nombre Producto': change.product_name || 'N/A',
    'Usuario': change.user_name || 'N/A',
    'Tipo de Precio': PRICE_TYPE_LABELS[change.price_type] || change.price_type,
    'Precio Anterior': Math.round(change.old_price),
    'Precio Nuevo': Math.round(change.new_price),
    'Diferencia': Math.round(change.new_price - change.old_price),
    'Cambio %': calculatePercentageChange(change.old_price, change.new_price),
    'Tipo Cambio': getChangeType(change.old_price, change.new_price),
    'Fecha y Hora': formatDateTime(change.timestamp)
  }));
};

export const exportPriceChangesToExcel = (
  changes: PriceChange[],
  startDate: string,
  endDate: string
): boolean => {
  try {
    // Transform data
    const data = transformPriceChangesData(changes);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // ID Producto
      { wch: 30 }, // Nombre Producto
      { wch: 20 }, // Usuario
      { wch: 15 }, // Tipo de Precio
      { wch: 15 }, // Precio Anterior
      { wch: 15 }, // Precio Nuevo
      { wch: 12 }, // Diferencia
      { wch: 10 }, // Cambio %
      { wch: 12 }, // Tipo Cambio
      { wch: 18 }  // Fecha y Hora
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Cambios de Precios');

    // Generate filename with date range
    const filename = `Cambios_Precios_${startDate}_a_${endDate}.xlsx`;

    // Write file
    XLSX.writeFile(wb, filename);

    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

export const exportFilteredPriceChanges = (
  changes: PriceChange[],
  startDate: string,
  endDate: string
): boolean => {
  if (changes.length === 0) {
    return false;
  }
  return exportPriceChangesToExcel(changes, startDate, endDate);
};
